import type { AlertColor } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { signatureBatchResendOtp, signatureSignOtp } from "../../../api/signer";

interface TokenSignatureModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  setMessageAlert: (string: string) => void;
  setTypeAlert: (string: AlertColor) => void;
  setOpenAlert: (boolean: boolean) => void;
  signatureType: "0" | "1" | "2" | "3";
  signerIdentifier?: string;
  onSuccess?: () => void;
}

export default function TokenSignatureModal({
  open,
  onClose,
  onBack,
  setMessageAlert,
  setOpenAlert,
  setTypeAlert,
  id,
  signatureType,
  signerIdentifier,
  onSuccess,
}: TokenSignatureModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [errorField, setErrorField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleClear = (): void => {
    setToken("");
    setOpenAlert(true);
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!token.trim()) return;
    setIsSubmitting(true);
    try {
      await signatureSignOtp(id, token.trim());
      setTypeAlert("success");
      setMessageAlert(t("chooseSignature.success"));
      handleClear();
      onSuccess?.();
    } catch (e) {
      setTypeAlert("error");
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 404) {
          setErrorField(t("chooseSignature.codeIncorrect"));
          return;
        }

        if (e.response?.status === 400) {
          setMessageAlert(t("chooseSignature.notSigner"));
          handleClear();
          return;
        }
      }
      setMessageAlert(t("chooseSignature.failed"));
      handleClear();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async (): Promise<void> => {
    if (!signerIdentifier) return;
    setIsResending(true);
    try {
      await signatureBatchResendOtp(id, signerIdentifier);
      setTypeAlert("success");
      setMessageAlert(t("tokenSignature.resendSuccess", "Código OTP reenviado com sucesso!"));
      setOpenAlert(true);
    } catch (e) {
      setTypeAlert("error");
      setMessageAlert(t("chooseSignature.failed", "Falha ao reenviar código OTP. Tente novamente."));
      setOpenAlert(true);
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setToken(event.target.value);
    if (errorField) {
      setErrorField("");
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("tokenSignature.title")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Typography
          sx={{ fontWeight: "400", fontSize: 14, color: "action.active" }}
        >
          {t("tokenSignature.tokenHelperText")}
        </Typography>
        <TextField
          error={Boolean(errorField)}
          fullWidth
          helperText={errorField}
          label={t("tokenSignature.tokenLabel")}
          onChange={handleChange}
          value={token}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{ width: "100%", display: "flex", flexDirection: "row", gap: 2 }}
        >
          <Button
            color="secondary"
            disabled={isResending || isSubmitting}
            onClick={() => void handleResendOtp()}
            sx={{ borderRadius: "12px", width: "100%" }}
            variant="outlined"
          >
            {isResending ? (
              <CircularProgress size={20} />
            ) : (
              t("tokenSignature.resendOtp", "Reenviar OTP")
            )}
          </Button>

          {signatureType !== "0" && (
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={onBack}
              sx={{ borderRadius: "12px", width: "100%" }}
              variant="outlined"
            >
              {t("namirial.changeSignatureType")}
            </Button>
          )}
          <Button
            color="primary"
            disabled={!token.trim() || isSubmitting}
            onClick={() => void handleConfirm()}
            sx={{ borderRadius: "12px", width: "100%" }}
            variant="contained"
          >
            {isSubmitting ? (
              <CircularProgress size={20} />
            ) : (
              t("tokenSignature.confirm", "Assinar com OTP")
            )}
          </Button>
        </Box>
        <Button
          color="inherit"
          disabled={isSubmitting}
          onClick={onClose}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("chooseSignature.back", "Voltar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
